<template>
    <canvas ref="canvas" class="bars" aria-hidden="true"></canvas>
</template>

<script>
const BAR_COUNT = 28
const BAR_GAP = 2
const MIN_HEIGHT = 2

export default {
    name: 'AssistantLevelBars',
    props: {
        // `{ read(count) }` from the recorder, or null when the browser has no
        // Web Audio — recording still works, it just does not draw.
        meter: {
            type: Object,
            default: null,
        },
        color: {
            type: String,
            default: '#967adc',
        },
    },
    data() {
        return {
            frame: null,
        }
    },
    mounted() {
        // Levels are drawn straight to the canvas and never kept in `data`:
        // sixty frames a second through the reactivity system would re-render
        // the panel on every one of them.
        this.resize()
        window.addEventListener('resize', this.resize)

        if (this.meter && !this.prefersReducedMotion()) this.draw()
    },
    // The linter asks for `beforeUnmount`, which does not exist on this Vue 2.6
    // runtime and would never fire, leaving the frame loop running.
    // eslint-disable-next-line vue/no-deprecated-destroyed-lifecycle
    beforeDestroy() {
        window.removeEventListener('resize', this.resize)

        if (this.frame) cancelAnimationFrame(this.frame)
    },
    methods: {
        prefersReducedMotion: function () {
            return window.matchMedia?.('(prefers-reduced-motion: reduce)')
                .matches
        },
        resize: function () {
            const canvas = this.$refs.canvas

            if (!canvas) return

            const ratio = window.devicePixelRatio || 1
            const { width, height } = canvas.getBoundingClientRect()

            canvas.width = Math.round(width * ratio)
            canvas.height = Math.round(height * ratio)
        },
        draw: function () {
            const canvas = this.$refs.canvas

            if (!canvas || !this.meter) return

            const context = canvas.getContext('2d')
            const { width, height } = canvas
            const ratio = window.devicePixelRatio || 1
            const gap = BAR_GAP * ratio
            const barWidth = (width - gap * (BAR_COUNT - 1)) / BAR_COUNT
            const levels = this.meter.read(BAR_COUNT)

            context.clearRect(0, 0, width, height)
            context.fillStyle = this.color

            levels.forEach((level, index) => {
                // Grown from the middle in both directions, so the row reads the
                // same whichever way the interface runs.
                const barHeight = Math.max(MIN_HEIGHT * ratio, level * height)
                const x = index * (barWidth + gap)
                const y = (height - barHeight) / 2
                const radius = Math.min(barWidth / 2, barHeight / 2)

                context.beginPath()

                if (context.roundRect) {
                    context.roundRect(x, y, barWidth, barHeight, radius)
                } else {
                    context.rect(x, y, barWidth, barHeight)
                }

                context.fill()
            })

            this.frame = requestAnimationFrame(this.draw)
        },
    },
}
</script>

<style scoped lang="scss">
.bars {
    flex-grow: 1;
    height: 28px;
    min-width: 0;
}
</style>
